import type { ReactNode } from "react";
import { AlertTriangle, LoaderCircle, RefreshCw } from "lucide-react";
import type { PagedState, QueryState } from "../api";
import { EmptyState, ErrorState } from "./EmptyState";

type Loadable =
  | Pick<QueryState<any>, "loading" | "error" | "stale" | "reload">
  | Pick<PagedState<any>, "loading" | "error" | "stale" | "reload">;

/**
 * Status line for a fetched list or record: a spinner while loading, a
 * clear error when the fetch failed, and an explicit "showing earlier data"
 * notice when the last good result is still on screen.
 */
export function DataState({ q, children }: { q: Loadable; children?: ReactNode }) {
  return (
    <>
      {q.error && (
        <p className={`data-state ${q.stale ? "stale" : "error-box"}`} role="alert">
          <AlertTriangle size={15} />
          {q.stale ? "Showing earlier data. " : ""}
          {q.error}{" "}
          <button className="text-button" onClick={() => q.reload()}>
            <RefreshCw size={13} /> Try again
          </button>
        </p>
      )}
      {q.loading && !q.error && (
        <p className="data-state loading">
          <LoaderCircle size={15} className="spin" /> Loading…
        </p>
      )}
      {children}
    </>
  );
}

/** "Load more" control for a cursor-paged list. */
export function Pager({ q, label = "entries" }: { q: PagedState<any>; label?: string }) {
  if (!q.items.length && !q.loading) return null;
  return (
    <div className="pager">
      <span className="muted">
        {q.items.length} {label} shown{q.hasMore ? " · more available" : ""}
      </span>
      {q.hasMore && (
        <button className="button small secondary" disabled={q.loading} onClick={() => q.loadMore()}>
          {q.loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

/** Grey placeholder rows while the first page loads (no layout jump when data arrives). */
export function Skeleton({ rows = 3, label = "Loading…" }: { rows?: number; label?: string }) {
  return (
    <div className="ui-skeleton" role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="ui-skeleton-row" aria-hidden="true" />
      ))}
    </div>
  );
}

/**
 * The four states of any fetched screen, the same way everywhere:
 * - first load: placeholder rows;
 * - failed with nothing to show: an error panel with "Try again";
 * - failed with earlier data: that data, under a "Showing earlier data" line;
 * - loaded and empty: the empty state (never a blank area).
 */
export function QueryView({
  q,
  hasData,
  isEmpty,
  empty,
  errorTitle,
  skeletonRows = 3,
  children,
}: {
  q: Loadable;
  /** Whether anything has loaded yet (data !== null, or items.length for a paged list). */
  hasData: boolean;
  /** Loaded, and nothing to list. */
  isEmpty?: boolean;
  empty?: ReactNode;
  errorTitle?: string;
  skeletonRows?: number;
  children: ReactNode;
}) {
  if (!hasData && q.error) return <ErrorState title={errorTitle} message={q.error} onRetry={() => void q.reload()} />;
  if (!hasData && q.loading) return <Skeleton rows={skeletonRows} />;
  if (!hasData) return null;
  return (
    <>
      {q.error && <DataState q={q} />}
      {isEmpty ? (empty ?? <EmptyState title="Nothing here yet" compact />) : children}
    </>
  );
}
